<template>
  <div class="ds-image-cloud" data-testid="ds-image-cloud">
    <DsLazyImage
      ref="dsLazyImageRef"
      :src="state.srcOptimized"
      :alt="alt"
      :populating="populating"
      :populating-class="populatingClass"
      :cover="cover"
      :mask="mask"
      @loaded="emit('loaded')"
      @error="emit('error')"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref, watch } from "vue";
import DsImageKitUrl from "../../helpers/image-kit-url/DsImageKitUrl";
import { DsLazyImage } from "../image";
import type { PropType } from "vue";

import type { DsMaskType } from "../../types";

defineOptions({
  name: "DsImageCloud",
});

const props = defineProps({
  alt: {
    type: String,
    default: "",
  },
  src: {
    type: String,
    default: "",
  },
  populating: {
    type: Boolean,
    default: false,
  },
  populatingClass: {
    type: String,
    default: "",
  },
  cover: {
    type: Boolean,
    default: true,
  },
  mask: {
    type: String as PropType<DsMaskType>,
    default: "square",
  },
});

const emit = defineEmits(["loaded", "error"]);

const dsLazyImageRef = ref<typeof DsLazyImage | null>(null);

const state = reactive({
  sizes: [300],
  size: 300,
  buckets: [],
  srcOptimized: undefined as undefined | string,
});

init();

function init() {
  if (import.meta.env.VITE_APP_IMAGEKIT_IMAGE_SIZES) {
    state.sizes = import.meta.env.VITE_APP_IMAGEKIT_IMAGE_SIZES.split(",").map(
      (size: string) => parseInt(size)
    );
  } else {
    console.warn(
      "Image sizes for ImageKit doesn't configured: Define VITE_APP_IMAGEKIT_IMAGE_SIZES in .env"
    );
  }

  if (import.meta.env.VITE_APP_S3_BUCKET_PUBLIC) {
    state.buckets = import.meta.env.VITE_APP_S3_BUCKET_PUBLIC.split(",").map(
      (bucket: string) => bucket
    );
  } else {
    console.warn(
      "S3 bucket for ImageKit doesn't configured: Define VITE_APP_S3_BUCKET_PUBLIC in .env"
    );
  }
}

function newSrc() {
  //Determine the image size according to its container.
  applySize();
  //Validate is URL and from S3 Bucket
  let validationUrl = false;
  state.buckets.forEach((bucket: string) => {
    if (props.src?.indexOf(bucket) > -1) {
      validationUrl = true;
    }
  });

  if (!props.src?.startsWith("http") || !validationUrl || !state.size) {
    state.srcOptimized = props.src;
  } else {
    if (props.src) {
      state.srcOptimized = DsImageKitUrl.get(
        props.src,
        DsImageKitUrl.getTransformationFromNumber(state.size)
      );
    }
  }
}

function applySize() {
  if (!dsLazyImageRef.value) {
    return;
  }

  const widthInner = (dsLazyImageRef.value?.$el as HTMLElement).offsetWidth;
  const heightInner = (dsLazyImageRef.value?.$el as HTMLElement).offsetHeight;
  const maxSize = state.sizes[state.sizes.length - 1];
  const maxContainerSize =
    widthInner > heightInner && widthInner < maxSize ? widthInner : heightInner;

  const sizesFinder = state.sizes.filter((e: number) => {
    return e >= maxContainerSize;
  });

  sizesFinder.sort((a: number, b: number) => a - b);
  if (sizesFinder.length) {
    state.size = sizesFinder[0];
  }
}

onMounted(() => {
  const ro = new ResizeObserver(() => {
    newSrc();
  });

  // Only observe the box
  ro.observe(dsLazyImageRef.value?.$el);

  //Load the original size required for the image.
  newSrc();
});

watch(
  () => props.src,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      newSrc();
    }
  }
);
</script>
